import { html, LitElement, nothing } from 'lit';

import { HIP, MixinElement } from '@eab-eip/hi-hip/hi-hif';
import { property, query } from 'lit/decorators.js';
import QRCode from 'qrcode';

/**
 * QR Code component
 *
 * @customElement
 * @element eab-oss-qr-code
 * @appliesMixin MixinElement
 */
export class QRCodeElement extends MixinElement(LitElement) {
  static get is() {
    return 'eab-oss-qr-code';
  }

  /**
   * the text that will be converted to a QR code
   * @attr
   */
  @property({ type: String })
  text = '';

  /**
   * QR Code options; See https://www.npmjs.com/package/qrcode#options to see the available options
   * @default {}
   * @attr
   */
  @property({ type: Object })
  options = {};

  /**
   *  flag to hide the copy button
   * @attr disallow-copy
   */
  @property({ type: Boolean, attribute: 'disallow-copy', reflect: true })
  disallowCopy = false;

  @query('#qr-code') qrCodeCanvasEl: HTMLCanvasElement;

  createRenderRoot() {
    return this;
  }

  render() {
    return html`
      <section l-kind="vert" l-justify="center" l-align="center" l-gutter>
        <canvas id="qr-code" role="img" aria-label="QR code for the provided text"></canvas>
        ${this.disallowCopy
          ? nothing
          : html`
              <hi-button
                aesthetic="secondary"
                @trigger-ended=${this.__copyQRCodeImageOnTriggerEnded}
                aria-describedby="qr-code"
              >
                <hi-icon glyph="copy"></hi-icon><span>Copy QR Code</span>
              </hi-button>
            `}
      </section>
    `;
  }

  firstUpdated() {
    this.computeQRCode();
  }

  /**
   * render text to a QR Code. Use this to update the code
   *
   * @returns Promise with the text rendered
   */
  computeQRCode() {
    return new Promise((resolve, reject) => {
      if (!this.qrCodeCanvasEl || !this.text) {
        reject('QR Code canvas element or text is not available');
      }

      QRCode.toCanvas(this.qrCodeCanvasEl, this.text, this.options, (err: unknown) => {
        if (err) {
          return reject(err);
        }

        resolve(this.text);
      });
    });
  }

  /**
   * send the QRCode to the clipboard
   *
   * @returns Promise<text> of the text sent to the clipboard
   */
  copyQRCode() {
    return new Promise((resolve, reject) => {
      if (!this.qrCodeCanvasEl) {
        return reject('QR Code canvas element not available');
      }

      QRCode.toDataURL(this.qrCodeCanvasEl, this.text, this.options, async (err: unknown, url) => {
        if (err) {
          HIP.fn.notify({ status: 'error', message: 'Failed to copy QR Code image to clipboard' });
          return reject(err);
        }

        const rawData = await fetch(url);
        const blob = await rawData.blob();
        const item = new ClipboardItem({ 'image/png': blob });

        await navigator.clipboard.write([item]);

        HIP.fn.notify({ status: 'success', message: 'QR Code image copied to clipboard' });
        resolve(this.text);
      });
    });
  }

  private __copyQRCodeImageOnTriggerEnded() {
    this.copyQRCode();
  }
}

if (!customElements.get(QRCodeElement.is)) {
  customElements.define(QRCodeElement.is, QRCodeElement);
}
